import React, { Component } from 'react';
import { CalendarCheckIcon, BlocksIcon, MonitorPlayIcon, TrendingUpIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
type Benefit = {
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  metric: string;
  metricLabel: string;
  title: string;
  desc: string;
  bullets: string[];
  accent: 'primary' | 'green' | 'amber' | 'rose';
  featured?: boolean;
};
const benefits: Benefit[] = [{
  icon: CalendarCheckIcon,
  metric: '6',
  metricLabel: 'Volumi completi',
  title: 'Percorso annuale 1ª-5ª classe',
  desc: 'Un sistema completo con progressione artistica reale per tutta la scuola primaria + Guida Docente.',
  bullets: ['Programmazione annuale pronta', 'Attività progressive per età', 'Obiettivi già organizzati'],
  accent: 'primary'
}, {
  icon: BlocksIcon,
  metric: '100+',
  metricLabel: 'Laboratori artistici',
  title: 'Arte viva e coinvolgente',
  desc: 'I bambini sperimentano colori, tecniche, materiali e creatività in modo divertente.',
  bullets: ['Pittura, collage e mosaici', 'Arte moderna e tecniche miste', 'Attività stagionali incluse'],
  accent: 'green'
}, {
  icon: MonitorPlayIcon,
  metric: '100%',
  metricLabel: 'Inclusione vera',
  title: 'Attività per BES & DSA',
  desc: 'Versioni facilitate, consegne semplificate e percorsi accessibili subito pronti.',
  bullets: ['Font ad alta leggibilità', 'Attività graduate', 'Supporti visuali inclusivi'],
  accent: 'amber'
}, {
  icon: TrendingUpIcon,
  metric: '100%',
  metricLabel: 'Tutto organizzato',
  title: 'Portfolio & Valutazione',
  desc: 'Rubriche, griglie e materiali già pronti per documentare il lavoro e la valutazione descrittiva.',
  bullets: ['Rubriche già compilate', 'Portfolio artistico', 'Materiale editabile'],
  accent: 'rose',
  featured: true
}];
const accentMap = {
  primary: {
    iconBg: 'bg-primary/10',
    iconBorder: 'border-primary/30',
    iconColor: 'text-primary',
    metric: 'text-primary',
    border: 'hover:border-primary/60'
  },
  green: {
    iconBg: 'bg-cta-green/10',
    iconBorder: 'border-cta-green/30',
    iconColor: 'text-cta-green',
    metric: 'text-cta-green',
    border: 'hover:border-cta-green/60'
  },
  amber: {
    iconBg: 'bg-secondary/10',
    iconBorder: 'border-secondary/30',
    iconColor: 'text-secondary',
    metric: 'text-secondary',
    border: 'hover:border-secondary/60'
  },
  rose: {
    iconBg: 'bg-badge-red/10',
    iconBorder: 'border-badge-red/30',
    iconColor: 'text-badge-red',
    metric: 'text-badge-red',
    border: 'hover:border-badge-red/60'
  }
};
export function BenefitsSection() {
  return <section className="w-full bg-bg-main py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-1">
      {/* Background accents */}
      <div className="absolute top-1/3 -left-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(234,88,12,0.08) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-2" />
      <div className="absolute bottom-0 -right-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(16,185,129,0.06) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-3" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-4">
        {/* Section header */}
        <div className="text-center mb-10 sm:mb-16" data-id="element-5">
          <span className="inline-flex items-center gap-2 text-primary font-black text-[11px] sm:text-xs uppercase tracking-[0.2em] bg-primary/10 border border-primary/20 px-4 py-1.5 rounded-full mb-4" data-id="element-6">
            🎁 Cosa c'è dentro
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-[1.1] mb-4" data-id="element-7">
            Tutto quello che ti serve
            <br className="hidden sm:block" data-id="element-8" />
            <span className="text-primary" data-id="element-9"> in un unico Kit.</span>
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-10">
            Quattro pilastri pensati per{' '}
            <span className="text-text-main font-bold" data-id="element-11">
              eliminare lo stress
            </span>{' '}
            e far brillare la tua classe.
          </p>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-12">
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          const accent = accentMap[benefit.accent];
          return <article key={i} className={`relative bg-bg-alt rounded-2xl sm:rounded-3xl border border-border-light p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 ${accent.border} ${benefit.featured ? 'shadow-[0_0_40px_rgba(239,68,68,0.10)]' : 'shadow-xl'}`} data-id="element-13">
                {/* Featured ribbon */}
                {benefit.featured && <div className="absolute -top-3 right-5 sm:right-6 bg-secondary text-bg-dark text-[9px] sm:text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border border-white/20 shadow-md" data-id="element-14">
                    ⭐ Risultato chiave
                  </div>}

                {/* Header: number + metric */}
                <div className="flex items-start justify-between mb-5 sm:mb-6" data-id="element-15">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${accent.iconBg} border ${accent.iconBorder} flex items-center justify-center flex-shrink-0`} data-id="element-16">
                    <Icon className={`w-5 h-5 sm:w-7 sm:h-7 ${accent.iconColor}`} strokeWidth={2.25} data-id="element-17" />
                  </div>
                  <div className="text-right" data-id="element-18">
                    <p className={`font-black text-3xl sm:text-4xl leading-none tracking-tighter ${accent.metric}`} data-id="element-19">
                      {benefit.metric}
                    </p>
                    <p className="text-text-sec text-[10px] sm:text-xs font-bold uppercase tracking-wider mt-1" data-id="element-20">
                      {benefit.metricLabel}
                    </p>
                  </div>
                </div>

                {/* Title + Description */}
                <h3 className="text-xl sm:text-2xl font-black text-text-main leading-tight mb-2.5" data-id="element-21">
                  {benefit.title}
                </h3>
                <p className="text-text-sec text-sm sm:text-base leading-relaxed mb-5" data-id="element-22">
                  {benefit.desc}
                </p>

                {/* Bullets */}
                <ul className="space-y-2 pt-4 border-t border-border-light" data-id="element-23">
                  {benefit.bullets.map((b, j) => <li key={j} className="flex items-start gap-2.5 text-text-main text-sm sm:text-[15px] font-medium leading-snug" data-id="element-24">
                      <span className={`flex items-center justify-center w-4 h-4 rounded-full ${accent.iconBg} border ${accent.iconBorder} flex-shrink-0 mt-0.5`} data-id="element-25">
                        <CheckIcon className={`w-2.5 h-2.5 ${accent.iconColor}`} strokeWidth={4} data-id="element-26" />
                      </span>
                      <span data-id="element-27">{b}</span>
                    </li>)}
                </ul>

                {/* Card number watermark */}
                <span className="absolute bottom-4 right-5 text-text-sec/15 font-black text-5xl sm:text-6xl leading-none tracking-tighter pointer-events-none select-none" data-id="element-28">
                  0{i + 1}
                </span>
              </article>;
        })}
        </div>

        {/* Bottom synthesis CTA */}
        <div className="mt-10 sm:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 bg-bg-alt border border-border-light rounded-2xl px-6 py-5 sm:py-6" data-id="element-29">
          <p className="text-text-main text-base sm:text-lg font-semibold text-center sm:text-left leading-snug" data-id="element-30">
            ⚡ Tutto questo arriva nella tua casella in{' '}
            <span className="text-cta-green font-black" data-id="element-31">30 secondi</span>{' '}
            dall'acquisto.
          </p>
          <a href="#pricing" style={{
          boxShadow: '0 0 20px rgba(57,255,20,0.55), 0 8px 24px rgba(57,255,20,0.35)'
        }} className="inline-flex items-center justify-center gap-2 font-black text-sm sm:text-base px-6 py-3 sm:px-7 sm:py-3.5 rounded-xl border-2 border-white/30 whitespace-nowrap uppercase tracking-tight transition-transform hover:scale-[1.03] active:scale-[0.98] bg-[#22C55E] text-[#FFFFFF]" data-id="element-32">
            Vedi l'offerta completa
            <ArrowRightIcon className="w-4 h-4" strokeWidth={3} data-id="element-33" />
          </a>
        </div>
      </div>
    </section>;
}